import { useCallback, useEffect, useRef, useState } from 'react';
import {
  buildMediaConstraints,
  listAvDevices,
  readDevicePrefs,
  saveDevicePrefs,
  type DeviceOption,
} from '@/lib/zcallDevices';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Test du matériel AVANT de rejoindre une réunion ZCall.
 * Ouvre la caméra + le micro en local (aucun envoi vers la salle), affiche
 * l'aperçu vidéo et un vumètre du micro, permet de choisir la caméra et le
 * micro quand l'appareil en propose plusieurs, puis libère le matériel.
 * Le choix est mémorisé et réutilisé à l'entrée dans la réunion.
 */
export default function CameraTestPanel({ open, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);

  const initialPrefs = useRef(readDevicePrefs());
  const cameraIdRef = useRef(initialPrefs.current.cameraId);
  const micIdRef = useRef(initialPrefs.current.micId);

  const [status, setStatus] = useState<'idle' | 'starting' | 'ready' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [level, setLevel] = useState(0);
  const [camOn, setCamOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [cameras, setCameras] = useState<DeviceOption[]>([]);
  const [mics, setMics] = useState<DeviceOption[]>([]);
  const [cameraId, setCameraId] = useState(initialPrefs.current.cameraId);
  const [micId, setMicId] = useState(initialPrefs.current.micId);

  useEffect(() => { cameraIdRef.current = cameraId; }, [cameraId]);
  useEffect(() => { micIdRef.current = micId; }, [micId]);

  const refreshDevices = useCallback(async () => {
    const { cameras: cams, mics: audios } = await listAvDevices();
    setCameras(cams);
    setMics(audios);
    return { cams, audios };
  }, []);

  const stopAll = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioCtxRef.current) {
      void audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setLevel(0);
    setStatus('idle');
  }, []);

  const startLevelMeter = useCallback((audioTrack: MediaStreamTrack) => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioCtxRef.current) {
      void audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    audioCtxRef.current = ctx;
    const source = ctx.createMediaStreamSource(new MediaStream([audioTrack]));
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    const buffer = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      analyser.getByteFrequencyData(buffer);
      let sum = 0;
      for (let i = 0; i < buffer.length; i += 1) sum += buffer[i];
      setLevel(Math.min(100, Math.round((sum / buffer.length / 128) * 100)));
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
  }, []);

  const start = useCallback(async (override?: { cameraId?: string; micId?: string }) => {
    setStatus('starting');
    setErrorMsg(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Votre navigateur ne permet pas l'accès à la caméra.");
      }
      // On libère le matériel précédent avant d'en ouvrir un autre.
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      const prefs = {
        cameraId: override?.cameraId ?? cameraIdRef.current,
        micId: override?.micId ?? micIdRef.current,
      };

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(buildMediaConstraints(prefs));
      } catch (firstErr) {
        const name = (firstErr as { name?: string } | null)?.name;
        // Le matériel mémorisé n'est plus branché : on repart sur le défaut.
        if (name === 'OverconstrainedError' || name === 'NotFoundError') {
          saveDevicePrefs({ cameraId: '', micId: '' });
          setCameraId('');
          setMicId('');
          stream = await navigator.mediaDevices.getUserMedia({
            video: { width: { ideal: 1280 }, height: { ideal: 720 } },
            audio: true,
          });
        } else {
          throw firstErr;
        }
      }

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setCamOn(stream.getVideoTracks().length > 0);
      setMicOn(stream.getAudioTracks().length > 0);
      setStatus('ready');

      // Mémorise le matériel réellement utilisé.
      const usedCam = stream.getVideoTracks()[0]?.getSettings().deviceId || '';
      const usedMic = stream.getAudioTracks()[0]?.getSettings().deviceId || '';
      if (usedCam && usedCam !== cameraIdRef.current) setCameraId(usedCam);
      if (usedMic && usedMic !== micIdRef.current) setMicId(usedMic);
      saveDevicePrefs({ cameraId: usedCam, micId: usedMic });

      await refreshDevices();

      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) startLevelMeter(audioTrack);
    } catch (err) {
      setStatus('error');
      const name = (err as { name?: string } | null)?.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setErrorMsg('Accès refusé. Autorisez la caméra et le microphone dans les réglages du navigateur, puis réessayez.');
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        setErrorMsg('Aucune caméra ou aucun microphone détecté sur cet appareil.');
      } else {
        setErrorMsg(err instanceof Error ? err.message : 'Test impossible.');
      }
    }
  }, [refreshDevices, startLevelMeter]);

  useEffect(() => {
    if (open) void start();
    else stopAll();
    return () => {
      if (!open) stopAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => () => stopAll(), [stopAll]);

  const pickCamera = (id: string) => {
    setCameraId(id);
    saveDevicePrefs({ cameraId: id });
    void start({ cameraId: id });
  };

  const pickMic = (id: string) => {
    setMicId(id);
    saveDevicePrefs({ micId: id });
    void start({ micId: id });
  };

  const toggleCam = () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setCamOn(track.enabled);
  };

  const toggleMic = () => {
    const track = streamRef.current?.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setMicOn(track.enabled);
  };

  if (!open) return null;

  const hasMultipleCameras = cameras.length > 1;
  const hasMultipleMics = mics.length > 1;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-lg bg-background-50 rounded-2xl p-5 md:p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2">
              <i className="ri-vidicon-line text-primary-500"></i>
              Test caméra &amp; micro
            </h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              Vérifiez votre image et votre son avant de rejoindre la réunion.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
            title="Fermer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="relative w-full h-52 md:h-60 rounded-xl overflow-hidden bg-foreground-950 mb-4">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-cover"
          />
          {status === 'starting' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/80">
              <i className="ri-loader-4-line animate-spin text-2xl mb-2"></i>
              <p className="text-xs">Activation du matériel…</p>
            </div>
          )}
          {status === 'error' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
              <i className="ri-video-off-line text-2xl text-white/60 mb-2"></i>
              <p className="text-xs text-white/80 leading-relaxed">{errorMsg}</p>
            </div>
          )}
          {status === 'ready' && !camOn && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70">
              <i className="ri-video-off-line text-2xl mb-2"></i>
              <p className="text-xs">Caméra coupée</p>
            </div>
          )}
          {status === 'ready' && (
            <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-black/50 text-white text-[10px] font-medium">
              Aperçu local
            </span>
          )}
        </div>

        {status === 'ready' && (
          <div className="space-y-3 mb-4">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0">
                <i className={micOn ? 'ri-mic-line' : 'ri-mic-off-line'}></i>
              </span>
              <div className="flex-1 h-2 rounded-full bg-background-200/70 overflow-hidden">
                <div
                  className="h-full rounded-full bg-accent-500 transition-[width] duration-100"
                  style={{ width: `${micOn ? level : 0}%` }}
                />
              </div>
              <span className="text-[11px] text-foreground-500 w-16 text-right whitespace-nowrap">
                {micOn ? (level > 6 ? 'Son détecté' : 'Parlez…') : 'Micro coupé'}
              </span>
            </div>

            {(hasMultipleCameras || hasMultipleMics) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-xl border border-background-200/70 bg-background-50 p-2.5">
                {hasMultipleCameras && (
                  <label className="block min-w-0">
                    <span className="block text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">
                      Caméra
                    </span>
                    <select
                      value={cameraId}
                      onChange={(e) => pickCamera(e.target.value)}
                      className="w-full px-2.5 py-2 bg-background-100 border border-background-200/70 rounded-lg text-xs text-foreground-800 cursor-pointer focus:outline-none focus:border-primary-300 truncate"
                    >
                      {cameras.map((c) => (
                        <option key={c.deviceId} value={c.deviceId}>{c.label}</option>
                      ))}
                    </select>
                  </label>
                )}
                {hasMultipleMics && (
                  <label className="block min-w-0">
                    <span className="block text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">
                      Microphone
                    </span>
                    <select
                      value={micId}
                      onChange={(e) => pickMic(e.target.value)}
                      className="w-full px-2.5 py-2 bg-background-100 border border-background-200/70 rounded-lg text-xs text-foreground-800 cursor-pointer focus:outline-none focus:border-primary-300 truncate"
                    >
                      {mics.map((m) => (
                        <option key={m.deviceId} value={m.deviceId}>{m.label}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                onClick={toggleCam}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  camOn ? 'bg-background-100 text-foreground-700 hover:bg-background-200/70' : 'bg-red-50 text-red-600 hover:bg-red-100'
                }`}
              >
                <i className={camOn ? 'ri-video-on-line' : 'ri-video-off-line'}></i>
                {camOn ? 'Couper la caméra' : 'Activer la caméra'}
              </button>
              <button
                onClick={toggleMic}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  micOn ? 'bg-background-100 text-foreground-700 hover:bg-background-200/70' : 'bg-red-50 text-red-600 hover:bg-red-100'
                }`}
              >
                <i className={micOn ? 'ri-mic-line' : 'ri-mic-off-line'}></i>
                {micOn ? 'Couper le micro' : 'Activer le micro'}
              </button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <button
            onClick={() => void start()}
            className="w-full mb-4 py-2.5 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line mr-1"></i>
            Réessayer
          </button>
        )}

        <button
          onClick={onClose}
          className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          C&apos;est bon, tout fonctionne
        </button>
        <p className="text-[10px] text-foreground-400 text-center mt-2">
          Rien n&apos;est envoyé aux autres participants pendant le test. Le matériel choisi sera utilisé pour la réunion.
        </p>
      </div>
    </div>
  );
}